import React, { useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import { getAdvisoryGroupAnalyticsData } from '../../../ducks/advisoryGroup';
import { AppLoadContext } from '../../../components/ui/AppLoadContext';
import DataGrid from '../../../components/DataGrid';
import withAuth from '../../../components/hoc/withAuth';
import Loader from '../../../components/ui/Loader';
import NavigationBoxDashboard from '../../../components/ui/NavigationBoxDashboard';
import OverflowText from "../../../components/ui/OverflowTooltip";
import { PROGRAM_TYPE, AGGREGATE_SCORES } from "../../../constants";
import GridButton from "../../../components/ui/GridButton";
import Icon from "../../../components/ui/Icon";
import GridCellRating from '../../../components/ui/GridCellRating';

const Analytics = () => {
    const dispatch = useDispatch();

    const isAppLoaded = useContext(AppLoadContext);
    const analyticsData = useSelector((state) => state.advisoryGroup.analyticsList);
    const currentUser = useSelector((state) => state.user.currentUser);

    useEffect(() => {
        if (isAppLoaded) {
            if (!currentUser) {
                return;
            }
            dispatch(getAdvisoryGroupAnalyticsData(currentUser?.companyId));
        }
        // Safe to add dispatch to the dependencies array
    }, [dispatch, isAppLoaded, currentUser]);

    const NavContent = [
        {
            path: "/analytics/population-characterization",
            locationName: "Advisory Group Management",
            pageName: 'Population Characterization',
            icon: 'population-characterization',
            classname: "purple",
            description: "Explore the characteristics of the member population represented by a specific Advisory Group."
        },
        {
            path: "/analytics/representation",
            locationName: "Advisory Group Management",
            pageName: 'Advisory Group Representation',
            icon: 'representativeness1',
            classname: "yellow",
            description: "View details on the representation of a specific Advisory Group including both the selection and attendance scorecards."
        }
    ]

    const columns = [
        {
            id: 'shortName',
            header: 'Advisory Group Name',
            accessorKey: 'shortName',
            cell: ({ row, cell }) => (
                <OverflowText id={cell.id}>
                    {row.original.shortName}
                </OverflowText>
            ),
            maxSize: 300,
            sortingFn: 'text'
        },
        {
            id: 'type',
            header: 'Advisory Group Type',
            accessorKey: 'type',
            accessorFn: (originalRow) => PROGRAM_TYPE[originalRow.type],
            cell: ({ row, cell }) => (
                <OverflowText id={cell.id}>
                    {PROGRAM_TYPE[row.original.type]}
                </OverflowText>
            ),
            sortingFn: 'text'
        },
        {
            id: 'aggregateScore',
            header: 'Representation Score',
            accessorKey: 'aggregateScore',
            accessorFn: (originalRow) => AGGREGATE_SCORES[originalRow.aggregateScore],
            cell: ({ row }) => <GridCellRating rating={row.original.aggregateScore} type={row.original.type} customClass="my-1" />,
            sortingFn: 'text',
            filterFn: 'equalsString',
        },
        {
            name: 'actions',
            header: 'Actions',
            size: 120,
            cell: () => (
                <GridButton variant="light" size="sm" className="me-1 my-1" title="View">
                    <Icon icon="view" size={16} />
                </GridButton>
            )
        },
    ];

    const defaultSort = [{
        id: 'shortName',
        desc: false
    }];

    return (
        <Row>
            <Col className='px-4'>
                <NavigationBoxDashboard data={NavContent} cols={2}/>
                <div className="d-flex flex-column mt-4">
                    <div className="flex-grow-1">
                        {analyticsData === null ? (
                            <Loader />
                        ) : (
                            <DataGrid
                                columns={columns}
                                data={analyticsData}
                                sort={defaultSort}
                            />
                        )}
                    </div>
                </div>
            </Col>
        </Row>
    );
};

export default withAuth(Analytics);
